"use client";

import { authFetch } from "@/lib/authFetch";
import { useEffect, useState, useRef } from "react";
import { useAuth } from "@/app/auth/AuthSessionProvider";
import { useTranslations } from "next-intl";
import { NotificationDropdown } from "./NotificationDropdown";
import { NotificationGlyphIcon } from "./NotificationGlyphIcon";

/**
 * NotificationBell renders the header bell (SVG outline, lime unread badge),
 * polls the unread count and toggles the notifications dropdown.
 */
export function NotificationBell() {
  const t = useTranslations("layout.notifications");
  const { isAuthenticated, isLoading } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isLoading || !isAuthenticated) {
      setUnreadCount(0);
      setLoading(false);
      return;
    }

    // Fetch unread notification count
    const fetchCount = async () => {
      try {
        const response = await authFetch(
          '/api/notifications/unread-count?category=transaction,auction,social',
          { cache: 'no-store' },
        );
        
        if (response.ok) {
          const data = await response.json();
          setUnreadCount(data.count || 0);
        }
      } catch (error) {
        console.error('[NotificationBell] Failed to fetch count:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCount();

    // Poll every 30 seconds for new notifications
    const interval = setInterval(fetchCount, 30000);
    
    return () => clearInterval(interval);
  }, [isAuthenticated, isLoading]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  if (isLoading || !isAuthenticated || loading) {
    return null;
  }

  const handleRefresh = (newCount: number) => {
    setUnreadCount(newCount);
  };

  return (
    <div className="gruv-notification-wrapper" ref={dropdownRef}>
      <button
        type="button"
        className={`gruv-notification-bell${isOpen ? ' gruv-notification-bell--open' : ''}${unreadCount > 0 ? ' gruv-notification-bell--unread' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        aria-label={t("aria.toggle")}
        aria-expanded={isOpen}
        aria-haspopup="dialog"
      >
        <NotificationGlyphIcon name="bell" size={22} className="gruv-notification-bell__icon" />
        {unreadCount > 0 && (
          <span className="gruv-notification-bell__badge" aria-label={t("aria.unread", { count: unreadCount })}>
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>
      
      {isOpen && (
        <NotificationDropdown 
          onClose={() => setIsOpen(false)} 
          onRefresh={handleRefresh}
        />
      )}
    </div>
  );
}
